import { useState, useEffect } from 'react';
import { X, Merge, AlertTriangle, Check, Search, ArrowRight } from 'lucide-react';
import { Badge, Button, Spinner } from '@centraoneplus/ui';
import type { Ticket } from '@centraoneplus/shared-types';

interface TicketMergeModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTickets: Ticket[];
  onMerge: (primaryTicketId: string, ticketIds: string[]) => Promise<void>;
  isLoading?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700',
  pending: 'bg-yellow-100 text-yellow-700',
  in_progress: 'bg-purple-100 text-purple-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200',
  new: 'bg-indigo-100 text-indigo-700',
};

const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  urgent: 'bg-red-100 text-red-600',
};

function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function TicketMergeModal({
  isOpen,
  onClose,
  selectedTickets,
  onMerge,
  isLoading = false,
}: TicketMergeModalProps) {
  const [primaryTicketId, setPrimaryTicketId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [step, setStep] = useState<'select' | 'confirm'>('select');

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setPrimaryTicketId(selectedTickets[0]?.id || null);
      setSearchQuery('');
      setStep('select');
    }
  }, [isOpen, selectedTickets]);

  if (!isOpen) return null;

  const filteredTickets = selectedTickets.filter(
    (ticket) =>
      ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.ticketNumber.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const primaryTicket = selectedTickets.find((t) => t.id === primaryTicketId);
  const secondaryTickets = selectedTickets.filter((t) => t.id !== primaryTicketId);

  const handleMerge = async () => {
    if (!primaryTicketId) return;
    const secondaryIds = secondaryTickets.map((t) => t.id);
    await onMerge(primaryTicketId, secondaryIds);
  };

  const handleNext = () => {
    if (primaryTicketId) {
      setStep('confirm');
    }
  };

  const handleBack = () => {
    setStep('select');
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900 rounded-lg">
                <Merge className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Merge Tickets
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedTickets.length} tickets selected
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg dark:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {step === 'select' && (
              <>
                {/* Warning */}
                <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-800 dark:text-amber-300">
                      This action cannot be undone
                    </p>
                    <p className="text-amber-700 dark:text-amber-400">
                      All conversations, attachments, and history from merged tickets will be
                      combined into the primary ticket. Secondary tickets will be closed.
                    </p>
                  </div>
                </div>

                {/* Search */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search tickets..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:placeholder-gray-500"
                    />
                  </div>
                </div>

                {/* Primary Ticket Selection */}
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Primary Ticket
                    <span className="text-gray-500 font-normal ml-1 dark:text-gray-400">
                      (this ticket will be kept)
                    </span>
                  </label>
                </div>

                {/* Ticket List */}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {filteredTickets.map((ticket) => (
                    <label
                      key={ticket.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        primaryTicketId === ticket.id
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="primaryTicket"
                        value={ticket.id}
                        checked={primaryTicketId === ticket.id}
                        onChange={() => setPrimaryTicketId(ticket.id)}
                        className="mt-1 h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500 dark:border-gray-700"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono text-gray-500 dark:text-gray-400">
                            {ticket.ticketNumber}
                          </span>
                          {primaryTicketId === ticket.id && (
                            <Badge variant="info">Primary</Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {ticket.subject}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              STATUS_COLORS[ticket.status] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                            }`}
                          >
                            {ticket.status}
                          </span>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              PRIORITY_COLORS[ticket.priority] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                            }`}
                          >
                            {ticket.priority}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Created {formatDate(ticket.createdAt)}
                          </span>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </>
            )}

            {step === 'confirm' && primaryTicket && (
              <>
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Merge Preview
                  </h3>

                  {/* Primary Ticket */}
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Check className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-green-800 dark:text-green-300">
                        Primary Ticket (will be kept)
                      </span>
                    </div>
                    <div className="pl-6">
                      <p className="text-sm font-mono text-gray-600 dark:text-gray-400">
                        {primaryTicket.ticketNumber}
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {primaryTicket.subject}
                      </p>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="flex items-center justify-center my-3">
                    <ArrowRight className="h-5 w-5 text-gray-400 rotate-90 dark:text-gray-500" />
                  </div>

                  {/* Secondary Tickets */}
                  <div className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Merge className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Tickets to merge ({secondaryTickets.length})
                      </span>
                    </div>
                    <div className="pl-6 space-y-2">
                      {secondaryTickets.map((ticket) => (
                        <div key={ticket.id} className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-gray-400" />
                          <span className="text-sm font-mono text-gray-600 dark:text-gray-400">
                            {ticket.ticketNumber}
                          </span>
                          <span className="text-sm text-gray-600 dark:text-gray-400">-</span>
                          <span className="text-sm text-gray-900 dark:text-white truncate">
                            {ticket.subject}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* What will happen */}
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                    What will happen:
                  </p>
                  <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1 list-disc list-inside">
                    <li>All conversations, messages and attachments will be moved into {primaryTicket.ticketNumber}, with an internal note recording each merge</li>
                    <li>{primaryTicket.ticketNumber} keeps its own status, assignee, priority, tags and fields</li>
                    <li>Tags and custom fields stay on the merged tickets and are not copied</li>
                    <li>
                      {secondaryTickets.length} ticket{secondaryTickets.length > 1 ? 's' : ''} will
                      be closed and marked as merged, linking to {primaryTicket.ticketNumber}
                    </li>
                  </ul>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-b-xl">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {step === 'select'
                ? `${selectedTickets.length} tickets selected`
                : `Merging ${secondaryTickets.length} ticket${
                    secondaryTickets.length > 1 ? 's' : ''
                  } into ${primaryTicket?.ticketNumber}`}
            </div>
            <div className="flex items-center gap-3">
              {step === 'confirm' && (
                <Button variant="outline" onClick={handleBack} disabled={isLoading}>
                  Back
                </Button>
              )}
              <Button variant="outline" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              {step === 'select' ? (
                <Button
                  variant="primary"
                  onClick={handleNext}
                  disabled={!primaryTicketId || selectedTickets.length < 2}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleMerge}
                  disabled={isLoading || !primaryTicketId}
                >
                  {isLoading ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Merging...
                    </>
                  ) : (
                    <>
                      <Merge className="h-4 w-4 mr-2" />
                      Merge Tickets
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
